import { secp256k1 } from '@noble/curves/secp256k1';
import { sha256 } from '@noble/hashes/sha256';
import { hmac } from '@noble/hashes/hmac';
import { extract, expand } from '@noble/hashes/hkdf';
import { chacha20 } from '@noble/ciphers/chacha';
import { b64decode, b64encode, bytesEqual, fromUtf8, randomBytes, utf8 } from './util.js';
import { normalizePubkey, normalizeSecret } from './keys.js';
import { hexToBytes } from './util.js';

/**
 * NIP-44 v2 payload encryption (ChaCha20 + HMAC-SHA256, encrypt-then-MAC).
 *
 * Used for Herald DIRECTED (transactional) messages so that the relay -- even a
 * relay the sender operates -- stores ciphertext only. Padding to power-of-two
 * buckets hides exact plaintext length.
 *
 * This is a from-spec implementation and MUST be validated against the official
 * NIP-44 test vectors before any production use; see spec/HP-06.
 */

const VERSION = 2;
const SALT = utf8('nip44-v2');

/**
 * ECDH conversation key. Symmetric: conversationKey(a, B) === conversationKey(b, A).
 * Only the x-coordinate of the shared point is used, hashed via HKDF-extract.
 */
export function getConversationKey(secretKey, peerPubkey) {
  const sk = normalizeSecret(secretKey);
  const pk = normalizePubkey(peerPubkey);
  // x-only pubkeys are implicitly even-y; prefix 0x02 to get a compressed point.
  const shared = secp256k1.getSharedSecret(sk, hexToBytes('02' + pk));
  return extract(sha256, shared.subarray(1, 33), SALT);
}

function messageKeys(conversationKey, nonce) {
  const keys = expand(sha256, conversationKey, nonce, 76);
  return {
    chachaKey: keys.subarray(0, 32),
    chachaNonce: keys.subarray(32, 44),
    hmacKey: keys.subarray(44, 76),
  };
}

/** NIP-44 padding scheme: pad to power-of-two buckets, minimum 32 bytes. */
export function calcPaddedLen(unpaddedLen) {
  if (unpaddedLen <= 0) throw new Error('plaintext must not be empty');
  if (unpaddedLen <= 32) return 32;
  const nextPower = 1 << (Math.floor(Math.log2(unpaddedLen - 1)) + 1);
  const chunk = nextPower <= 256 ? 32 : nextPower / 8;
  return chunk * (Math.floor((unpaddedLen - 1) / chunk) + 1);
}

export const MAX_PLAINTEXT_BYTES = 4294967295;

/**
 * Length prefix, per NIP-44's extended form.
 *
 * Lengths below 65536 use a two-byte big-endian prefix. At or above that, the
 * two bytes are zero and a four-byte length follows. A zero u16 is unambiguous
 * because a zero-length plaintext is invalid, so the two forms cannot collide.
 *
 * Supporting the extended form matters for interoperability: a peer using a
 * current NIP-44 implementation can produce a payload larger than 64 KiB, and
 * without this branch it would be undecryptable here rather than merely large.
 */
function pad(plaintext) {
  const unpadded = utf8(plaintext);
  const len = unpadded.length;
  if (len < 1 || len > MAX_PLAINTEXT_BYTES) {
    throw new Error(`plaintext length must be 1..${MAX_PLAINTEXT_BYTES} bytes`);
  }
  const prefixLen = len < 65536 ? 2 : 6;
  const padded = new Uint8Array(prefixLen + calcPaddedLen(len));

  if (prefixLen === 2) {
    padded[0] = (len >>> 8) & 0xff;
    padded[1] = len & 0xff;
  } else {
    // padded[0..2] stay zero, marking the extended form.
    padded[2] = (len >>> 24) & 0xff;
    padded[3] = (len >>> 16) & 0xff;
    padded[4] = (len >>> 8) & 0xff;
    padded[5] = len & 0xff;
  }
  padded.set(unpadded, prefixLen);
  return padded;
}

function unpad(padded) {
  if (padded.length < 2) throw new Error('padded payload too short');
  const short = (padded[0] << 8) | padded[1];

  let len;
  let prefixLen;
  if (short === 0) {
    if (padded.length < 6) throw new Error('padded payload too short for an extended length');
    // >>> 0 keeps the value unsigned; a 32-bit length can exceed 2^31.
    len = ((padded[2] << 24) | (padded[3] << 16) | (padded[4] << 8) | padded[5]) >>> 0;
    prefixLen = 6;
    if (len < 65536) throw new Error('invalid padding: extended length used for a short plaintext');
  } else {
    len = short;
    prefixLen = 2;
  }
  if (len < 1 || len > MAX_PLAINTEXT_BYTES) throw new Error('invalid padding: implausible length');

  const body = padded.subarray(prefixLen, prefixLen + len);
  if (body.length !== len || padded.length !== prefixLen + calcPaddedLen(len)) {
    throw new Error('invalid padding');
  }
  return fromUtf8(body);
}

function hmacWithAad(key, message, aad) {
  const buf = new Uint8Array(aad.length + message.length);
  buf.set(aad, 0);
  buf.set(message, aad.length);
  return hmac(sha256, key, buf);
}

/**
 * @param {string} plaintext
 * @param {Uint8Array} conversationKey from getConversationKey()
 * @returns {string} base64 payload
 */
export function encrypt(plaintext, conversationKey, nonce = randomBytes(32)) {
  const { chachaKey, chachaNonce, hmacKey } = messageKeys(conversationKey, nonce);
  const padded = pad(plaintext);
  const ciphertext = chacha20(chachaKey, chachaNonce, padded);
  const mac = hmacWithAad(hmacKey, ciphertext, nonce);

  const payload = new Uint8Array(1 + 32 + ciphertext.length + 32);
  payload[0] = VERSION;
  payload.set(nonce, 1);
  payload.set(ciphertext, 33);
  payload.set(mac, 33 + ciphertext.length);
  return b64encode(payload);
}

/**
 * @param {string} payload base64 from encrypt()
 * @param {Uint8Array} conversationKey
 * @returns {string} plaintext
 */
export function decrypt(payload, conversationKey) {
  if (typeof payload !== 'string' || payload.length < 132) throw new Error('payload too short');
  if (payload[0] === '#') throw new Error('unsupported payload encoding');
  const bytes = b64decode(payload);
  if (bytes[0] !== VERSION) throw new Error(`unsupported nip44 version: ${bytes[0]}`);
  if (bytes.length < 99) throw new Error('payload too short');

  const nonce = bytes.subarray(1, 33);
  const ciphertext = bytes.subarray(33, bytes.length - 32);
  const mac = bytes.subarray(bytes.length - 32);

  const { chachaKey, chachaNonce, hmacKey } = messageKeys(conversationKey, nonce);
  // Encrypt-then-MAC: authenticate before touching the ciphertext.
  if (!bytesEqual(hmacWithAad(hmacKey, ciphertext, nonce), mac)) {
    throw new Error('invalid MAC (wrong key or tampered payload)');
  }
  return unpad(chacha20(chachaKey, chachaNonce, ciphertext));
}

/** Convenience: encrypt directly from a keypair + peer pubkey. */
export function encryptTo(plaintext, secretKey, peerPubkey) {
  return encrypt(plaintext, getConversationKey(secretKey, peerPubkey));
}

export function decryptFrom(payload, secretKey, peerPubkey) {
  return decrypt(payload, getConversationKey(secretKey, peerPubkey));
}
